import React, { Suspense, lazy, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { renderRoutes } from 'react-router-config';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import IframePopup from '@wix/da-shared-react/pkg/IframePopup';
import isSSR from '@wix/da-shared-react/pkg/utils/isSSR';

import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { PapiUser } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { Url, USER_PROFILE_BACKROOM_SECTIONS } from '@wix/da-url';

import SubSubNavBar from '../../SubSubNavBar';

const ActionLog = lazy(
  () => import(/* webpackChunkName: "backroom" */ './_partials/ActionLog')
);
const MessageLog = lazy(
  () => import(/* webpackChunkName: "backroom" */ './_partials/MessageLog')
);
const GroupNotes = lazy(
  () => import(/* webpackChunkName: "backroom" */ './_partials/GroupNotes')
);

import s from './BackroomSection.scss';

export interface Props {
  profileOwnerUser: PapiUser;
  canManageRoles: boolean;
  canRespondToGroupNotifications: boolean;
}

const BackroomSection: React.FC<Props> = ({
  profileOwnerUser,
  canManageRoles,
  canRespondToGroupNotifications,
}) => {
  const [showRoster, setShowRoster] = useState(false);
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const defaultRoute = useMemo(
    () => ({
      label: t('common.notes'),
      component: GroupNotes,
    }),
    [t]
  );
  const tabRoutes = useMemo(() => {
    const { username: ownerUsername } = profileOwnerUser;
    const username = ownerUsername.toLowerCase();
    return [
      canRespondToGroupNotifications && {
        label: t('common.notes'),
        path: Url.groupBackroomPath(
          username,
          USER_PROFILE_BACKROOM_SECTIONS.NOTES
        ),
        component: GroupNotes,
      },
      {
        label: t('common.messagelog'),
        path: Url.groupBackroomPath(
          username,
          USER_PROFILE_BACKROOM_SECTIONS.MESSAGE_LOG
        ),
        component: MessageLog,
      },
      {
        label: t('common.actionlog'),
        path: Url.groupBackroomPath(
          username,
          USER_PROFILE_BACKROOM_SECTIONS.ACTION_LOG
        ),
        component: ActionLog,
      },
    ].filter(x => x);
  }, [t, profileOwnerUser, canRespondToGroupNotifications]);

  const routes = useMemo(
    () => [...tabRoutes, defaultRoute],
    [defaultRoute, tabRoutes]
  );

  return (
    <div className={s['root']}>
      {!isMobile && (
        <div className={s['navbar']}>
          <SubSubNavBar className={s['section-nav']} items={tabRoutes} />
          {canManageRoles && (
            <GhostButton
              size={'large'}
              variant={'main'}
              onClick={() => setShowRoster(true)}
              className={s['btn-roster']}
            >
              {t('common.manageMembers')}
            </GhostButton>
          )}
        </div>
      )}
      {!isSSR() && (
        <Suspense fallback={<LoadingIndicator view="llama" />}>
          {renderRoutes(routes)}
        </Suspense>
      )}

      {showRoster && (
        <Modal
          isOpen
          showFooterBorder
          showHeaderBorder
          heading={t('common.manageMembers')}
          onRequestClose={() => setShowRoster(false)}
          isFullscreen={true}
        >
          <IframePopup
            style={{
              position: 'relative',
              height: '100%',
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px',
            }}
            src={Url.groupRosterLink(profileOwnerUser.username)}
          />
        </Modal>
      )}
    </div>
  );
};
BackroomSection.displayName = 'BackroomSection';
export default BackroomSection;
